import type { SuggestField } from "../api/items.js";
import { useSuggestions } from "../hooks/useSuggestions.js";

/**
 * A labelled text input backed by `GET /api/items/suggestions`
 * (debounced — see `useSuggestions`), replacing the legacy
 * `AutoCompleteComboBox` fields in `AddItemWindow.xaml`.
 *
 * Built on a native `<input list>` + `<datalist>` rather than a custom
 * combobox widget: it is keyboard-operable and labelled for free, needs no
 * bespoke popup/roving-focus logic, and typing a value that isn't in the
 * suggestion list is always allowed (exactly like the legacy control, which
 * suggested but never restricted input). The trade-off is that not every
 * screen reader announces `<datalist>` options equally well — a real one,
 * flagged for follow-up once this needs to go further than "good enough".
 */
export function AutocompleteInput({
  id,
  label,
  field,
  value,
  onChange,
  uppercase = false,
  required = false,
  disabled = false,
  invalid = false,
}: {
  id: string;
  label: string;
  field: SuggestField;
  value: string;
  onChange: (value: string) => void;
  /** Live-uppercases as typed, matching legacy's `CharacterCasing="Upper"` / `.ToUpper()` fields — never applied to phone numbers. */
  uppercase?: boolean;
  required?: boolean;
  disabled?: boolean;
  /** Flags the input as failing a required-field check (red border + `aria-invalid`), set on a save attempt. */
  invalid?: boolean | undefined;
}) {
  const suggestions = useSuggestions(field, value);
  const listId = `${id}-suggestions`;

  return (
    <div className="form-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        list={listId}
        value={value}
        onChange={(e) => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
        required={required}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        autoComplete="off"
      />
      <datalist id={listId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </div>
  );
}
